import {
  LitElement,
  html,
  css,
  classMap,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
} from '@hh/global.js';

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
    size: {
      type: String,
    },
    clickOutsideClose: {
      type: Boolean,
      attribute: 'click-outside-close',
    },
    hideClose: {
      type: Boolean,
      attribute: 'hide-close',
    },
    insideClose: {
      type: Boolean,
      attribute: 'inside-close',
    },
  }

  constructor() {
    super()

    this.open = false
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })

    window.addEventListener('keyup', this.close.bind(this))
    if (this.clickOutsideClose) {
      document.addEventListener('click', this.close.bind(this))
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()
    window.removeEventListener('keyup', this.close.bind(this))
    if (this.clickOutsideClose) {
      document.removeEventListener('click', this.close.bind(this))
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      this.dispatchToggleEvent()
    }
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true
    } else {
      this.open = false
      return
    }

    if (data.element) {
      const element = this.querySelector(`[id="${data.element}"]`)
      setTimeout(() => {
        if (data.select_element) {
          element?.click()
        }
        element?.scrollIntoView({
          behavior: 'instant',
        })
      }, 0)
    }

    if (data.custom_event) {
      this.dispatchEvent(
        new CustomEvent(data.custom_event, {
          bubbles: true,
          composed: true,
          detail: data,
        })
      )
    }

    if (data.auto_close) {
      if (this.autoCloseTimeout) clearTimeout(this.autoCloseTimeout)

      if (data.auto_close !== typeof 'number') {
        data.auto_close = 2500
      }

      this.autoCloseTimeout = setTimeout(() => {
        this.open = false
        this.removeScrollLock()
      }, data.auto_close)
    }
  }

  closeFromModal(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.open = false
      this.removeScrollLock()
    }
  }

  close(event) {
    if (!this.open) return false
    if (event.type === 'click') {
      if (this.contains(event.target)) return
      this.open = false
    }

    if (event.type === 'keyup') {
      event.code.toUpperCase() === 'ESCAPE' && (this.open = false)
    }

    this.removeScrollLock()
  }

  removeScrollLock() {
    document.documentElement.classList.remove('overflow-hidden')
  }

  dispatchToggleEvent() {
    this.dispatchEvent(
      new CustomEvent(`modal:${this.open ? 'opened' : 'closed'}`, {
        bubbles: true,
        composed: true,
        detail: {
          id: this.id,
          open: this.open,
        },
      })
    )
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      @click=${this.closeFromModal}
    >
      <button
        @click="${this.close}"
        class=${classMap({
          button: true,
          hide: this.hideClose,
          inside: this.insideClose,
        })}
        type="button"
        aria-label="${t('accessibility.close')}"
      >
        ${unsafeHTML(paths.close)}
      </button>
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >
        <hh-focus-trap active="${this.open}">
          <slot></slot>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      height: auto;
      max-width: 95%;
      top: revert;
      bottom: var(--hh-flyout-offset, 30px);
      left: 50%;
      transform: translateX(-50%);
      border: 0.1rem solid var(--hh-border-color);
      border-radius: var(--hh-border-radius, var(--hh-spacing-xs));
      box-shadow: var(--hh-box-shadow, var(--hh-default-box-shadow));
    }

    @media (min-width: 640px) {
      :host([size='flyout']) {
        max-width: var(--hh-max-width, 400px);
        right: var(--hh-flyout-offset, 30px);
        left: revert;
        transform: initial;
      }
    }

    :host([size='modal']) {
      width: 100%;
      height: 100%;
      background: rgba(var(--hh-color-night-rgb), 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
    }


    :host([size='modal']) [role='dialog'] {
      width: 95%;
      max-height: 100%;
      min-height: 0;
      background: var(--hh-background);
      border-radius: var(--hh-border-radius, var(--hh-spacing-xs));
      box-shadow: var(--hh-box-shadow, var(--hh-default-box-shadow));
    }

    :host([size='modal']) .content {
      height: 100%;
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    [role='dialog'] {
      margin: auto;
      max-height: var(--hh-max-height, 100%);
      max-width: var(--hh-max-width, 100%);
      position: var(--hh-position, static);
    }

    .button {
      width: 4rem;
      height: 4rem;
      position: var(--hh-close-button-position, fixed);
      top: var(--hh-close-button-top, 4rem);
      right: var(--hh-close-button-right, 4rem);
      display: var(--hh-close-display, flex);
      background-color: var(
        --hh-button-background,
        var(--hh-background, transparent)
      );
      border: 0.1rem solid var(--hh-border-color);
      border-radius: 50%;
      color: var(--hh-button-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 1.2rem;
      z-index: 2;
    }

    .button .icon {
      width: 100%;
      height: 100%;
    }

    .button.hide {
      display: none;
    }

    .button.inside {
      border: 0;
      background-color: transparent;
      top: calc(var(--hh-close-button-top, 4rem) + 2rem);
      right: var(--hh-close-button-right, 4rem); 
    }
    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100dvh;
      justify-content: center;
      overflow: auto;
      width: 100%;
      padding: var(--hh-padding);
    }

    :host([size='flyout']) .content {
      height: auto;
      padding: 0;
    }

    :host([size='pop-up']) .content hh-focus-trap {
      display: flex;
      height: min-content;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('hh-modal', HHModal)

document.addEventListener("DOMContentLoaded", () => {
  const modal = document.getElementById("loginModal");
  const closeButton = modal?.shadowRoot?.querySelector(".button.inside");
  const wishlistContainer = modal?.querySelector(".wishlist-modal__container");

  if (modal && closeButton && wishlistContainer) {
    const updateButtonPosition = () => {
      const rect = wishlistContainer.getBoundingClientRect();
      closeButton.style.top = `${rect.top}px`;
    };

    updateButtonPosition();

    window.addEventListener("resize", updateButtonPosition);
    window.addEventListener("scroll", updateButtonPosition);
  }
});